import React, { useState, useEffect, useRef } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { Camera, Loader2, Upload, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB — matches backend limit
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

// Auto-resize large photos down to fit the 5 MB limit so phone uploads "just work".
// Uses a canvas to scale + re-encode as JPEG at progressively lower quality.
async function compressImage(file) {
  // Small enough? Skip work.
  if (file.size <= MAX_FILE_BYTES) return file;
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  // Target longest edge — 1920 looks crisp even on retina screens.
  const MAX_EDGE = 1920;
  let { width: w, height: h } = img;
  if (Math.max(w, h) > MAX_EDGE) {
    const ratio = MAX_EDGE / Math.max(w, h);
    w = Math.round(w * ratio);
    h = Math.round(h * ratio);
  }
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);

  // Try progressively lower JPEG quality until we're under the limit
  for (const q of [0.85, 0.75, 0.65, 0.55, 0.45]) {
    // eslint-disable-next-line no-await-in-loop
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', q));
    if (blob && blob.size <= MAX_FILE_BYTES) {
      return new File([blob], (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    }
  }
  // Couldn't get small enough — return original; backend will reject if too big.
  return file;
}

const Gallery = () => {
  const { currentUser } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [alt, setAlt] = useState('');
  const [tags, setTags] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);

  const canUpload = currentUser && (currentUser.role === 'admin' || currentUser.role === 'barber');

  const [barbers, setBarbers] = useState([]);
  const [filterBarberId, setFilterBarberId] = useState(''); // '' = all
  const [selectedBarberId, setSelectedBarberId] = useState('');

  useEffect(() => {
    const loadBarbers = async () => {
      try {
        const res = await api.get('/api/barbers');
        setBarbers(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to load barbers', e);
      }
    };
    loadBarbers();
  }, []);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const qs = filterBarberId ? `&barber_id=${encodeURIComponent(filterBarberId)}` : '';
      const response = await api.get(`/api/gallery/images?limit=50${qs}`);
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGallery(); }, [filterBarberId]);

  const handleFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPEG, PNG, or WebP allowed');
      return;
    }

    let finalFile = file;
    if (file.size > MAX_FILE_BYTES) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      const tId = toast.loading(`Compressing photo (${sizeMB} MB)…`);
      try {
        finalFile = await compressImage(file);
      } catch (err) {
        console.error('compress err', err);
      }
      toast.dismiss(tId);
      if (finalFile.size > MAX_FILE_BYTES) {
        toast.error('Photo is still too large after compression — try a smaller one.');
        return;
      }
      const newMB = (finalFile.size / 1024 / 1024).toFixed(1);
      toast.success(`Compressed: ${sizeMB} MB → ${newMB} MB`);
    }

    setUploadFile(finalFile);
    setUploadPreview(URL.createObjectURL(finalFile));
    if (!alt) setAlt(finalFile.name.replace(/\.[^.]+$/, '').slice(0, 80));
  };

  const closeUploadDialog = () => {
    setUploadOpen(false);
    setUploadFile(null);
    if (uploadPreview) URL.revokeObjectURL(uploadPreview);
    setUploadPreview(null);
    setAlt('');
    setTags('');
    setSelectedBarberId('');
  };

  const openUploadDialog = () => {
    // Barbers default to crediting themselves
    if (currentUser?.role === 'barber' && currentUser.barber_id) {
      setSelectedBarberId(currentUser.barber_id);
    }
    setUploadOpen(true);
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Pick an image first');
      return;
    }
    if (!alt.trim()) {
      toast.error('Add a short caption');
      return;
    }
    try {
      setUploading(true);
      const form = new FormData();
      form.append('image', uploadFile);
      form.append('alt', alt.trim());
      if (tags.trim()) form.append('tags', tags.trim());
      // Attribute photo to selected barber (admin chooses; barber defaults to self)
      const attribBarber = selectedBarberId
        || (currentUser?.role === 'barber' ? currentUser.barber_id : '');
      if (attribBarber) form.append('barber_id', attribBarber);
      const res = await api.post('/api/gallery/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Image uploaded');
      closeUploadDialog();
      // Optimistic prepend + refetch
      if (res.data?.image) setImages((prev) => [res.data.image, ...prev]);
      fetchGallery();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (image) => {
    if (!window.confirm(`Delete this image? "${image.alt}"`)) return;
    try {
      setDeletingId(image.id);
      await api.delete(`/api/gallery/images/${image.id}`);
      setImages((prev) => prev.filter((i) => i.id !== image.id));
      if (selectedImage?.id === image.id) setSelectedImage(null);
      toast.success('Image deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const canDelete = (image) =>
    currentUser && (currentUser.role === 'admin' || image.created_by === currentUser.id);

  return (
    <div className="sauce-sity-page" data-testid="gallery-page">
      <div className="sauce-sity-content">
        <div className="ss-section-header ss-mb-48">
          <h1 className="ss-title-xl">
            <span className="ss-highlight-green">Our</span> Work
          </h1>
          <p className="ss-section-subtitle">Fresh cuts, clean fades, premium style</p>

          {canUpload && (
            <button
              onClick={openUploadDialog}
              className="ss-btn ss-btn-primary ss-mt-24"
              data-testid="gallery-upload-btn"
            >
              <Upload size={18} />
              Upload Image
            </button>
          )}
        </div>

        {/* Barber filter chips */}
        {!loading && (barbers.length > 0) && (
          <div
            style={{
              display: 'flex', flexWrap: 'wrap', gap: 8,
              justifyContent: 'center', marginBottom: 24,
            }}
            data-testid="gallery-barber-filter"
          >
            <button
              onClick={() => setFilterBarberId('')}
              data-testid="gallery-filter-all"
              style={{
                padding: '8px 16px', borderRadius: 999, fontSize: '0.85rem', fontWeight: 600,
                background: filterBarberId === '' ? 'var(--ss-green)' : 'rgba(255,255,255,0.06)',
                color: filterBarberId === '' ? '#000' : '#fff',
                border: filterBarberId === '' ? '1px solid var(--ss-green)' : '1px solid rgba(255,255,255,0.15)',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              All Barbers
            </button>
            {barbers.map((b) => (
              <button
                key={b.id}
                onClick={() => setFilterBarberId(b.id)}
                data-testid={`gallery-filter-${b.id}`}
                style={{
                  padding: '8px 16px', borderRadius: 999, fontSize: '0.85rem', fontWeight: 600,
                  background: filterBarberId === b.id ? 'var(--ss-green)' : 'rgba(255,255,255,0.06)',
                  color: filterBarberId === b.id ? '#000' : '#fff',
                  border: filterBarberId === b.id ? '1px solid var(--ss-green)' : '1px solid rgba(255,255,255,0.15)',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80 }}>
            <Loader2 size={48} className="animate-spin" style={{ color: 'var(--ss-green)' }} />
          </div>
        )}

        {!loading && images.length > 0 && (
          <div className="ss-grid-3" data-testid="gallery-grid">
            {images.map((image) => (
              <div
                key={image.id}
                className="ss-card"
                style={{ padding: 0, overflow: 'hidden', cursor: 'pointer', position: 'relative' }}
                data-testid={`gallery-item-${image.id}`}
                onClick={() => setSelectedImage(image)}
              >
                {canDelete(image) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(image); }}
                    disabled={deletingId === image.id}
                    aria-label="Delete image"
                    data-testid={`gallery-delete-${image.id}`}
                    style={{
                      position: 'absolute', top: 8, right: 8, zIndex: 2,
                      background: 'rgba(0,0,0,0.65)', color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: 999, padding: 6, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {deletingId === image.id
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Trash2 size={14} />}
                  </button>
                )}
                <div style={{ position: 'relative', aspectRatio: '1 / 1', overflow: 'hidden' }}>
                  <img
                    src={image.url}
                    alt={image.alt}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  />
                </div>
                <div style={{ padding: 14 }}>
                  <p style={{ color: 'var(--ss-white-soft)', fontSize: '0.875rem', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {image.alt}
                  </p>
                  {image.barber_name && (
                    <p style={{ color: 'var(--ss-green)', fontSize: '0.78rem', fontWeight: 600, margin: '4px 0 0 0' }}>
                      ✂ by {image.barber_name}
                    </p>
                  )}
                  {image.tags && image.tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                      {image.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          style={{
                            background: 'rgba(161,0,255,0.18)',
                            color: 'var(--ss-purple)',
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                          }}
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && images.length === 0 && (
          <div className="ss-card ss-card-purple" data-testid="gallery-empty" style={{ textAlign: 'center', padding: 64 }}>
            <Camera size={56} style={{ margin: '0 auto', color: 'var(--ss-white-soft)' }} />
            <h3 className="ss-title-md ss-mt-24">No Images Yet</h3>
            <p className="ss-text">Gallery images will appear here</p>
          </div>
        )}

        {/* Lightbox */}
        {selectedImage && (
          <div
            onClick={() => setSelectedImage(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)',
              zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
            data-testid="gallery-modal"
          >
            <div style={{ position: 'relative', maxWidth: 1100, width: '100%' }} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setSelectedImage(null)}
                data-testid="gallery-modal-close"
                className="ss-btn ss-btn-secondary"
                style={{ position: 'absolute', top: -60, right: 0, minHeight: 40, padding: '0 16px' }}
              >
                <X size={18} /> Close
              </button>
              <img
                src={selectedImage.url}
                alt={selectedImage.alt}
                style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 'var(--ss-radius-lg)', border: '1px solid rgba(170,255,0,0.22)' }}
              />
              <div className="ss-text-center ss-mt-24">
                <h3 className="ss-title-md">{selectedImage.alt}</h3>
                {selectedImage.barber_name && (
                  <p style={{ color: 'var(--ss-green)', fontWeight: 600 }}>by {selectedImage.barber_name}</p>
                )}
                {canDelete(selectedImage) && (
                  <button
                    onClick={() => handleDelete(selectedImage)}
                    disabled={deletingId === selectedImage.id}
                    className="ss-btn ss-btn-secondary ss-mt-24"
                    data-testid="gallery-modal-delete"
                    style={{ background: 'rgba(220, 38, 38, 0.15)', borderColor: 'rgba(220, 38, 38, 0.5)', color: '#fca5a5' }}
                  >
                    {deletingId === selectedImage.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    Delete image
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Upload dialog */}
        {uploadOpen && (
          <div
            onClick={closeUploadDialog}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
              zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
            data-testid="gallery-upload-modal"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: 480, width: '100%',
                background: 'linear-gradient(160deg, rgba(40,0,60,0.95), rgba(15,15,15,0.95))',
                border: '1px solid rgba(170,255,0,0.3)',
                borderRadius: 18, padding: 24, position: 'relative',
                boxShadow: '0 20px 60px rgba(170,255,0,0.15)',
              }}
            >
              <button
                onClick={closeUploadDialog}
                aria-label="Close"
                style={{
                  position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.5)',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: 999, padding: 6,
                  color: '#fff', cursor: 'pointer',
                }}
                data-testid="gallery-upload-cancel-btn"
              >
                <X size={16} />
              </button>

              <h3 className="ss-title-md" style={{ marginBottom: 16 }}>Upload to Gallery</h3>

              {/* File picker */}
              {!uploadPreview ? (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%', padding: 32, borderRadius: 12,
                    border: '2px dashed rgba(170,255,0,0.4)', background: 'rgba(170,255,0,0.05)',
                    color: 'var(--ss-white-soft)', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  }}
                  data-testid="gallery-upload-file-btn"
                >
                  <Upload size={36} style={{ color: 'var(--ss-green)' }} />
                  <p style={{ margin: 0, fontWeight: 600 }}>Tap to choose a photo</p>
                  <p style={{ margin: 0, fontSize: '0.78rem', opacity: 0.7 }}>JPEG / PNG / WebP · large photos auto-compressed</p>
                </button>
              ) : (
                <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
                  <img src={uploadPreview} alt="preview"
                    style={{ width: '100%', maxHeight: 240, objectFit: 'cover', display: 'block' }} />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.7)',
                      color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: 999, padding: '6px 12px', fontSize: '0.78rem', cursor: 'pointer',
                    }}
                    data-testid="gallery-upload-change-file-btn"
                  >
                    Change
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFilePicked}
                style={{ display: 'none' }}
                data-testid="gallery-upload-file-input"
              />

              <label style={{ display: 'block', marginTop: 14, fontSize: '0.85rem', color: 'var(--ss-white-soft)' }}>
                Caption *
                <input
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  placeholder="e.g. Skin fade with curly top"
                  maxLength={120}
                  style={{
                    width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 10,
                    background: 'rgba(0,0,0,0.5)', color: '#fff',
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  data-testid="gallery-upload-caption-input"
                />
              </label>

              <label style={{ display: 'block', marginTop: 14, fontSize: '0.85rem', color: 'var(--ss-white-soft)' }}>
                Tags <span style={{ opacity: 0.6 }}>(comma-separated, optional)</span>
                <input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="fade, beard, lineup"
                  style={{
                    width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 10,
                    background: 'rgba(0,0,0,0.5)', color: '#fff',
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  data-testid="gallery-upload-tags-input"
                />
              </label>

              {/* Tag the barber */}
              <label style={{ display: 'block', marginTop: 14, fontSize: '0.85rem', color: 'var(--ss-white-soft)' }}>
                Tag barber <span style={{ opacity: 0.6 }}>(optional)</span>
                <select
                  value={selectedBarberId}
                  onChange={(e) => setSelectedBarberId(e.target.value)}
                  style={{
                    width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 10,
                    background: 'rgba(0,0,0,0.5)', color: '#fff',
                    border: '1px solid rgba(255,255,255,0.15)',
                    appearance: 'none',
                    backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2214%22%20height%3D%2214%22%20viewBox%3D%220%200%2014%2014%22%3E%3Cpath%20fill%3D%22%23aaffaa%22%20d%3D%22M3.5%205.5l3.5%203.5%203.5-3.5z%22%2F%3E%3C%2Fsvg%3E")',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    paddingRight: 36,
                  }}
                  data-testid="gallery-upload-barber-select"
                >
                  <option value="">— No barber tag —</option>
                  {barbers.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </label>

              <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
                <button
                  onClick={closeUploadDialog}
                  disabled={uploading}
                  className="ss-btn ss-btn-secondary"
                  style={{ flex: 1 }}
                  data-testid="gallery-upload-cancel-action-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || !uploadFile}
                  className="ss-btn ss-btn-primary"
                  style={{ flex: 1 }}
                  data-testid="gallery-upload-submit-btn"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {uploading ? 'Uploading…' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
